import { useMemo } from 'react';
import {
	useVaultsStore,
	VaultStatWithConfig,
} from '../../stores/vaultsStore/useVaultsStore';
import { VaultDepositor } from '@drift-labs/vaults-sdk';

export type VaultStatsWithDepositor = {
	vaultStat: VaultStatWithConfig;
	depositor: VaultDepositor | null;
};

/**
 * Hook that combines vault stats with their corresponding depositor accounts.
 * This avoids having to loop through depositor accounts every time we need to match them with vault stats.
 * @returns An array of vault stats with their matched depositor accounts (null if no match)
 */
export const useVaultStatsWithDepositors = () => {
	const vaultsStatsList = useVaultsStore((s) => s.getVaultsStatsList());
	const authorityDepositedVaults = useVaultsStore(
		(s) => s.authorityDepositedVaults
	);

	return useMemo(() => {
		// Create a map of vault pubkey to depositor for faster lookups
		const depositorMap = new Map<string, VaultDepositor>();
		authorityDepositedVaults.forEach((vault) => {
			depositorMap.set(
				vault.vaultDepositor.vault.toString(),
				vault.vaultDepositor
			);
		});

		// Combine vault stats with their depositors
		return vaultsStatsList.map((vaultStat): VaultStatsWithDepositor => {
			const vaultPubkey = vaultStat.uiVaultConfig.vaultPubkeyString;
			const depositor = depositorMap.get(vaultPubkey) || null;

			return {
				vaultStat,
				depositor,
			};
		});
	}, [vaultsStatsList, authorityDepositedVaults]);
};
